import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { PatientCardLink } from '../../PatientCardLink';
import { execApi } from '../../../services/api';
import { buildReportCsv, type GranRow } from './ReportModal';

// ONE unified filter panel (Ohad 2026-07-23). The user configures every facet — period,
// role, kind, status, clinic, therapist(s), summary/detailed — then presses "הצג נתונים";
// NOTHING is fetched automatically on a filter change (the default view IS loaded once on
// entry). Both the summary matrix and the detailed list are driven by the SAME applied
// selection. Empty set = "all" for every facet, so the "הכל" chip is the default-on state.

const PATIENT_URL = 'https://clinicui.taliaz-cm.com/patient';
const PAGE_SIZE = 50;

const STATUS_LABEL: Record<string, string> = { done: 'בוצע', noshow: 'no show', canceled: 'בוטל', active: 'פעילה' };
const STATUS_COLOR: Record<string, string> = { done: '#10B981', noshow: '#EF4444', canceled: '#F59E0B', active: '#2563EB' };
// SIX disjoint kinds (Ohad 2026-07-26). מעקב means a plain ADULT follow-up: youth, a
// double and a re-intake are each their own bucket, so ticking every chip sums to the
// total and "how many follow-ups" is deliberately מעקב + מעקב נוער + מעקב כפול.
// The derived ones use payroll's definitions — see exec_meetings._MTG_KIND_CASE.
// `followupAdhoc` split out of `followup` on 2026-07-30 (Ohad): a chip beside מעקב
// rather than a filter of its own. The kinds stay DISJOINT — מעקב no longer
// includes an ad-hoc one — so ticking every chip still sums to the total.
const KIND_ORDER = ['intake', 'intakeYouth', 'followup', 'followupAdhoc', 'followupYouth', 'double', 'reintake'];
const KIND_LABEL: Record<string, string> = {
  intake: 'אינטייק', intakeYouth: 'אינטייק נוער',
  followup: 'מעקב', followupAdhoc: 'מעקב אד-הוק', followupYouth: 'מעקב נוער',
  double: 'מעקב כפול', reintake: 'ריאינטייק',
};
// The two derived kinds are coloured wherever they appear, so they stand out from the
// two ordinary ones without needing a badge of their own.
const KIND_COLOR: Record<string, string> = { double: 'var(--ex-blue-text)', reintake: '#92400E', followupAdhoc: '#0E7490' };
const ROLE_LABEL: Record<string, string> = { psy: 'פסיכ׳', cm: 'CM' };
// The detailed row says exactly what the summary matrix and the filter say — role +
// one of the six kinds ("פסיכ׳ · מעקב כפול"). The row's own `type` from the backend is
// the four-bucket meeting type; it is used only for the ROLE half, the kind comes from
// the derived `kind`, so a row can never be labelled differently from the number above it.
const roleOfType = (t: string) => (t.startsWith('cm') ? 'cm' : 'psy');
const rowKindLabel = (t: string, k: string) =>
  `${ROLE_LABEL[roleOfType(t)]} · ${KIND_LABEL[k] ?? '—'}`;
// Deep links from other dashboards still arrive as ?type=psyFu — the old bucket names.
const LEGACY_TYPE: Record<string, { role: string; kind: string }> = {
  psyIntake: { role: 'psy', kind: 'intake' }, psyFu: { role: 'psy', kind: 'followup' },
  cmIntake: { role: 'cm', kind: 'intake' }, cmFu: { role: 'cm', kind: 'followup' },
};
const WHO_LABEL: Record<string, string> = { staff: 'צוות', client: 'לקוח', unknown: '—' };
const STATUS_ORDER = ['done', 'canceled', 'noshow', 'active'];
const ROLES = ['psy', 'cm'];
const bucketOf = (role: string, kind: string) => `${role}:${kind}`;
const TYPE_ORDER = ROLES.flatMap((r) => KIND_ORDER.map((k) => bucketOf(r, k)));
// Summary-matrix row label: role + kind, e.g. "פסיכ׳ · מעקב כפול".
const TYPE_LABEL: Record<string, string> = Object.fromEntries(
  TYPE_ORDER.map((b) => [b, `${ROLE_LABEL[b.split(':')[0]]} · ${KIND_LABEL[b.split(':')[1]]}`]));
const BUCKET_ROLE: Record<string, string> = Object.fromEntries(
  ROLES.flatMap((r) => KIND_ORDER.map((k) => [bucketOf(r, k), r])));
const BUCKET_KIND: Record<string, string> = Object.fromEntries(
  ROLES.flatMap((r) => KIND_ORDER.map((k) => [bucketOf(r, k), k])));
// Role → meetingtype_ids. The KIND half is no longer expressible as ids: two of the four
// kinds are derived and cut across the types (a re-intake is usually a follow-up type),
// so the backend takes `kinds` as its own parameter and this carries the role only.
const ROLE_IDS: Record<string, number[]> = { psy: [2, 3, 5], cm: [1, 4] };
const ROLE_OPTS = [{ value: 'psy', label: 'פסיכיאטר' }, { value: 'cm', label: 'מנהל תיק' }];
const KIND_OPTS = KIND_ORDER.map((k) => ({ value: k, label: KIND_LABEL[k] }));
const STATUS_OPTS = STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s] }));
const HE_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const MONTH_OPTS = (() => {
  const out: { id: string; label: string }[] = [];
  const now = new Date();
  for (let i = 11 - now.getMonth(); i >= -17; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    out.push({ id: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: `${HE_MONTHS[d.getMonth()]} ${d.getFullYear()}` });
  }
  return out;
})();

const CARD: React.CSSProperties = { background: 'var(--ex-bg-card)', border: '1px solid var(--ex-border)', borderRadius: 12 };
const fmtDate = (iso: string) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};
const fmtLead = (h: number | null) => (h == null ? '—' : h < 48 ? `${h} ש׳` : `${Math.round(h / 24)} ימים`);

// Empty set = "all". Membership test used everywhere the applied filter narrows data.
const hit = (sel: Set<string>, v: string) => sel.size === 0 || sel.has(v);
const setKey = (s: Set<string>) => Array.from(s).sort().join('|');

type Applied = {
  months: string[];
  roles: Set<string>; kinds: Set<string>; statuses: Set<string>; clinics: Set<string>; therapists: Set<string>;
  mode: 'summary' | 'detailed'; q: string;
  compare: string[];
  // Day-level span inside the month window (YYYY-MM-DD, '' = unset) and the
  // "slot already passed" switch. Both narrow the summary AND the list together.
  dateFrom: string; dateTo: string; pastOnly: boolean;
};

const PanelField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
    <span style={{ fontSize: 12, color: 'var(--ex-text-muted)', fontWeight: 600, minWidth: 52 }}>{label}</span>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

const chipBtn = (label: string, on: boolean, onClick: () => void, key?: string) => (
  <button key={key ?? label} type="button" onClick={onClick}
    style={{ padding: '3px 14px', borderRadius: 999, fontSize: 12.5, cursor: 'pointer', border: `1px solid ${on ? 'var(--ex-blue-border)' : 'var(--ex-border)'}`, background: on ? 'var(--ex-blue)' : 'transparent', color: on ? 'var(--ex-blue-text)' : 'var(--ex-text-muted)', fontWeight: on ? 700 : 400 }}>
    {label}
  </button>
);

// Map a deep-link ?type= value (bucket or role) to role/kind draft sets.
function typeParamToSets(t: string): { roles: Set<string>; kinds: Set<string> } {
  if (t === 'psy' || t === 'cm') return { roles: new Set([t]), kinds: new Set() };
  if (LEGACY_TYPE[t]) return { roles: new Set([LEGACY_TYPE[t].role]), kinds: new Set([LEGACY_TYPE[t].kind]) };
  return { roles: new Set(), kinds: new Set() };
}

export function MeetingsExplorer({ backMonths, clinics }: { backMonths: string[]; clinics?: string[] }) {
  // Read once for the whole table — usePermissions fetches on mount, and this list
  // runs to hundreds of rows (Ohad 2026-09-22).
  const canOpenPatient = usePermissions().can('patient_card');
  const [sp] = useSearchParams();
  const curMonthId = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;

  // ── Initial draft (from deep-link params, else defaults = current month + all + summary) ──
  const initMonths = sp.get('months') ? sp.get('months')!.split(',').filter(Boolean) : (backMonths.length ? backMonths : [curMonthId]);
  const initType = typeParamToSets(sp.get('type') || '');
  // Cancellations are OFF by default (Ohad, 2026-09-28: "כרגע זה לא מעניין אותנו").
  // An explicit selection rather than a hidden rule in the query: the filter panel
  // shows "בוטלו" unticked, so the exclusion is visible and one click undoes it —
  // and the whole cancellation analysis (refill rate, who cancelled) comes back with
  // it. The status matrix follows the same applied set, so the canceled column leaves
  // the screen too. A deep-link carrying ?status= still wins, including ?status=canceled.
  const initStatus = sp.get('status') && STATUS_ORDER.includes(sp.get('status')!)
    ? new Set([sp.get('status')!])
    : new Set<string>(STATUS_ORDER.filter((s) => s !== 'canceled'));
  const initTherapist = sp.get('therapist') ? new Set([sp.get('therapist')!]) : new Set<string>();

  // Draft filters (what the user is editing) — empty set = all.
  const [dMonths, setDMonths] = useState<string[]>(initMonths);
  const [dRoles, setDRoles] = useState<Set<string>>(initType.roles);
  const [dKinds, setDKinds] = useState<Set<string>>(initType.kinds);
  const [dStatuses, setDStatuses] = useState<Set<string>>(initStatus);
  const [dClinics, setDClinics] = useState<Set<string>>(new Set());
  const [dTherapists, setDTherapists] = useState<Set<string>>(initTherapist);
  const [dMode, setDMode] = useState<'summary' | 'detailed'>('summary');
  const [qDraft, setQDraft] = useState('');

  // ── Column-header controls on the detailed table (Ohad 2026-07-26) ──
  // These act IMMEDIATELY — they are not part of the draft/"הצג נתונים" cycle. Clicking a
  // header is a direct manipulation of the table in front of you, so making it wait for a
  // second button would feel broken; the panel above stays the way you set up the query.
  const [colPatientTypes, setColPatientTypes] = useState<Set<string>>(new Set());
  const [colWhos, setColWhos] = useState<Set<string>>(new Set());
  // Shachar's ad_hoc flag (2026-07-30). A column-only facet like the two above:
  // one value filters, both or none means don't care.

  const [sort, setSort] = useState('date_desc');
  const [openCol, setOpenCol] = useState<string | null>(null);
  // Draft values for the day span + the passed-slot switch (applied on "הצג נתונים").
  const [dFrom, setDFrom] = useState('');
  const [dTo, setDTo] = useState('');
  const [dPastOnly, setDPastOnly] = useState(false);
  // Months to compare the period against. Picking any turns the summary into a
  // month-by-month table — the picker IS the switch, so there is no separate
  // "compare" toggle to forget you left on (Ohad, 2026-09-10).
  const [dCompare, setDCompare] = useState<string[]>([]);
  const [cmpOpen, setCmpOpen] = useState(false);
  // Which summary row has its per-status breakdown open. Comparing is the question
  // "did it move"; the split by status is a second question, and it waits to be asked.
  const [openType, setOpenType] = useState<string | null>(null);

  // Applied snapshot (drives every fetch + render) — starts = the initial draft (auto first load).
  const [applied, setApplied] = useState<Applied>(() => ({
    months: initMonths, roles: initType.roles, kinds: initType.kinds, statuses: initStatus,
    clinics: new Set(), therapists: initTherapist, mode: 'summary', q: '',
    dateFrom: '', dateTo: '', pastOnly: false, compare: [],
  }));
  const [page, setPage] = useState(1);

  // Dropdown open states.
  const [monthsOpen, setMonthsOpen] = useState(false);
  const [thOpen, setThOpen] = useState(false);
  const [thQuery, setThQuery] = useState('');

  // ── Data ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [gran, setGran] = useState<GranRow[]>([]);
  const [granLoading, setGranLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [listData, setListData] = useState<any>(null);
  const [listLoading, setListLoading] = useState(false);

  const monthsKey = applied.months.join(',');
  // One request covers the period and its comparison: the server groups by month
  // anyway, so asking for both together costs nothing over asking for one.
  const windowMonths = useMemo(
    () => Array.from(new Set([...applied.months, ...applied.compare])).sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthsKey, applied.compare.join(',')]);
  const comparing = applied.compare.length > 0;

  // Summary source: granular counts for the applied window (fetched once per window).
  // A range with no month ticked is a legitimate window — the server treats a range as
  // REPLACING the months, so requiring a month here would block the wider question.
  useEffect(() => {
    if (!windowMonths.length && !applied.dateFrom && !applied.dateTo) { setGran([]); return; }
    let alive = true; setGranLoading(true);
    execApi.getMeetingsReport(windowMonths, applied.dateFrom, applied.dateTo)
      .then((r) => { if (alive) setGran((r.data ?? []) as GranRow[]); })
      .catch(() => { if (alive) setGran([]); })
      .finally(() => { if (alive) setGranLoading(false); });
    return () => { alive = false; };
    // `applied` is the snapshot taken when "הצג נתונים" was pressed, so applied.months
    // keeps its identity between applies — listing it costs no extra fetch.
  }, [windowMonths, monthsKey, applied.dateFrom, applied.dateTo]);

  // Detailed-list request params, derived from the applied selection.
  // Role only — the kind travels separately (see ROLE_IDS).
  const appliedTypeIds = useMemo(() => {
    const roles = ROLES.filter((r) => hit(applied.roles, r));
    if (roles.length === ROLES.length) return [] as number[];   // all → no constraint
    return Array.from(new Set(roles.flatMap((r) => ROLE_IDS[r])));
  }, [applied]);
  const appliedStatuses = useMemo(() => Array.from(applied.statuses), [applied]);
  const appliedTherapists = useMemo(() => Array.from(applied.therapists), [applied]);
  const appliedClinics = useMemo(() => (applied.clinics.size ? Array.from(applied.clinics) : undefined), [applied]);
  const appliedKinds = useMemo(() => Array.from(applied.kinds), [applied]);

  const colPts = useMemo(() => Array.from(colPatientTypes), [colPatientTypes]);
  const colWhoList = useMemo(() => Array.from(colWhos), [colWhos]);

  const listKey = JSON.stringify([monthsKey, applied.dateFrom, applied.dateTo, applied.pastOnly,
    appliedTypeIds, appliedStatuses, appliedTherapists, appliedClinics ?? [], appliedKinds, colPts, colWhoList, sort, applied.q, applied.mode, page]);
  useEffect(() => {
    if (applied.mode !== 'detailed' || (!applied.months.length && !applied.dateFrom && !applied.dateTo)) {
      setListData(null); return;
    }
    let alive = true; setListLoading(true);
    execApi.getMeetingsExplorer({
      backMonths: applied.months, clinics: appliedClinics, typeIds: appliedTypeIds,
      statuses: appliedStatuses, therapists: appliedTherapists, kinds: appliedKinds,
      patientTypes: colPts, whos: colWhoList, sort,
      q: applied.q, page, pageSize: PAGE_SIZE,
      dateFrom: applied.dateFrom, dateTo: applied.dateTo, pastOnly: applied.pastOnly,
    })
      .then((r) => { if (alive) setListData(r.data); })
      .catch(() => { if (alive) setListData(null); })
      .finally(() => { if (alive) setListLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey]);

  // Picker option lists come from the (applied-months) gran.
  const clinicOptions = useMemo(() => Array.from(new Set(gran.map((r) => r.clinic))).filter(Boolean).sort(), [gran]);
  const therapistOptions = useMemo(() => Array.from(new Set(gran.map((r) => r.therapist))).filter(Boolean).sort(), [gran]);
  // The therapist picker follows the role/type chosen right next to it. It used to
  // filter on the search box alone, so picking "פסיכיאטר" and then opening the list
  // still offered every case manager (Ohad, 2026-08-30). Read off the DRAFT role, not
  // the applied one — the two selectors sit in the same bar and the user is mid-choice,
  // long before "הצג נתונים".
  //
  // An already-selected therapist stays listed even once out of scope, or changing the
  // role would leave them silently filtering the results with no way to un-tick them.
  const thInScope = useMemo(() => new Set(
    gran.filter((r) => hit(dRoles, r.role) && hit(dKinds, r.kind)).map((r) => r.therapist),
  ), [gran, dRoles, dKinds]);
  const thShown = therapistOptions.filter(
    (t) => (thInScope.has(t) || dTherapists.has(t))
      && t.toLowerCase().includes(thQuery.toLowerCase()));

  // ── Apply / dirty ──
  const draftKey = [dMonths.join(','), setKey(dRoles), setKey(dKinds), setKey(dStatuses), setKey(dClinics), setKey(dTherapists), dMode, qDraft, dFrom, dTo, String(dPastOnly), dCompare.join(',')].join('#');
  const appliedKey = [applied.months.join(','), setKey(applied.roles), setKey(applied.kinds), setKey(applied.statuses), setKey(applied.clinics), setKey(applied.therapists), applied.mode, applied.q, applied.dateFrom, applied.dateTo, String(applied.pastOnly), applied.compare.join(',')].join('#');
  const dirty = draftKey !== appliedKey;

  const applyNow = () => {
    setApplied({
      months: [...dMonths], roles: new Set(dRoles), kinds: new Set(dKinds), statuses: new Set(dStatuses),
      clinics: new Set(dClinics), therapists: new Set(dTherapists),
      mode: dMode, q: qDraft,
      dateFrom: dFrom, dateTo: dTo, pastOnly: dPastOnly, compare: [...dCompare],
    });
    setPage(1);
  };

  // Generic "הכל"+chips group (empty = all; first click on a specific value narrows to it).
  const groupChips = (options: { value: string; label: string }[], sel: Set<string>, setSel: (s: Set<string>) => void) => {
    const allOn = sel.size === 0;
    const clickVal = (v: string) => {
      if (sel.size === 0) { setSel(new Set([v])); return; }
      const n = new Set(sel);
      if (n.has(v)) n.delete(v); else n.add(v);
      setSel(n.size === 0 || n.size === options.length ? new Set() : n);
    };
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {chipBtn('הכל', allOn, () => setSel(new Set()), '__all')}
        {options.map((o) => chipBtn(o.label, !allOn && sel.has(o.value), () => clickVal(o.value), o.value))}
      </div>
    );
  };

  // Therapist dropdown item click — same empty=all semantics as groups.
  const clickTherapist = (name: string) => {
    if (dTherapists.size === 0) { setDTherapists(new Set([name])); return; }
    const n = new Set(dTherapists);
    if (n.has(name)) n.delete(name); else n.add(name);
    setDTherapists(n.size === 0 || n.size === therapistOptions.length ? new Set() : n);
  };
  const thLabel = dTherapists.size === 0 ? 'כל המטפלים' : `${dTherapists.size} מטפלים`;

  const toggleMonth = (id: string) => setDMonths((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]));
  // A range beats the month picker. Stated once here so the label, the styling and the
  // "החודשים מבוטלים" note can never disagree with what the server actually queries.
  const rangeWins = !!(dFrom || dTo);
  const monthsLabel = dMonths.length === 0 ? 'בחר חודש' : dMonths.length === 1 ? MONTH_OPTS.find((m) => m.id === dMonths[0])?.label : `${dMonths.length} חודשים`;

  // ── Summary (from applied filters over gran) ──
  const granSel = useMemo(() => gran.filter((r) =>
    hit(applied.roles, r.role) && hit(applied.kinds, r.kind) && hit(applied.statuses, r.status)
    && hit(applied.clinics, r.clinic) && hit(applied.therapists, r.therapist)),
    [gran, applied]);
  // Options for a column menu = the values that actually survive the OTHER filters.
  // A facet must not narrow itself, or opening "מטפל" while a therapist is selected
  // would offer only that one therapist and there would be no way back.
  const granExcept = (facet: 'roles' | 'kinds' | 'statuses' | 'clinics' | 'therapists') => gran.filter((r) =>
    (facet === 'roles' || hit(applied.roles, r.role))
    && (facet === 'kinds' || hit(applied.kinds, r.kind))
    && (facet === 'statuses' || hit(applied.statuses, r.status))
    && (facet === 'clinics' || hit(applied.clinics, r.clinic))
    && (facet === 'therapists' || hit(applied.therapists, r.therapist)));

  const visStatuses = STATUS_ORDER.filter((s) => hit(applied.statuses, s));
  const selTypes = TYPE_ORDER.filter((t) => hit(applied.roles, BUCKET_ROLE[t]) && hit(applied.kinds, BUCKET_KIND[t]));
  const statusCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of granSel) c[r.status] = (c[r.status] ?? 0) + r.n;
    return c;
  }, [granSel]);
  const matrix = useMemo(() => {
    const m: Record<string, Record<string, number>> = {};
    for (const t of TYPE_ORDER) m[t] = {};
    for (const r of granSel) { const b = bucketOf(r.role, r.kind); if (m[b]) m[b][r.status] = (m[b][r.status] ?? 0) + r.n; }
    return m;
  }, [granSel]);

  // Working hours per type, from the same rows the counts come from — so the hours
  // column can never disagree with the row it sits on (Avi 2026-09-24, Ohad 2026-09-28).
  // Cancellations are excluded here even if the status filter lets them in: a slot
  // nobody sat in is not time worked, and that is what this column claims to measure.
  const hoursByType = useMemo(() => {
    const h: Record<string, number> = {};
    for (const t of TYPE_ORDER) h[t] = 0;
    for (const r of granSel) {
      if (r.status === 'canceled') continue;
      const b = bucketOf(r.role, r.kind);
      if (b in h) h[b] += r.minutes;
    }
    return h;
  }, [granSel]);
  const fmtHours = (min: number) =>
    (min / 60).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Who did them. "כמה מעקבי אד הוק נעשו ע״י כל מנהל טיפול? לא מצאתי דרך" — Shira,
  // 2026-10-05. The summary answered by TYPE and the detailed list answered row by
  // row, and neither answered "per person". Same granSel the matrix above uses, so it
  // obeys whatever is filtered — pick מעקב אד-הוק and this is the ad-hoc table.
  const byTherapist = useMemo(() => {
    const m: Record<string, { n: number; minutes: number; role: string }> = {};
    for (const r of granSel) {
      if (r.status === 'canceled') continue;      // a slot nobody sat in is not work done
      const e = m[r.therapist] || (m[r.therapist] = { n: 0, minutes: 0, role: r.role });
      e.n += r.n;
      e.minutes += r.minutes;
    }
    return Object.entries(m)
      .map(([therapist, v]) => ({ therapist, ...v }))
      .sort((a, b) => b.n - a.n);
  }, [granSel]);
  // Share of the row, and ONLY on no-show (Ohad, 2026-10-04). It briefly sat on every
  // status, which made the matrix a wall of percentages where three of the four say
  // nothing anyone asks: "what share of this type was performed" is answered by the
  // count beside it. The no-show rate is the one that is read as a rate.
  // Empty string at zero rather than "0.00%" — a nought beside a nought is noise.
  // Denominator = meetings somebody DECIDED on: done + noshow. Not the row total.
  // A meeting still ahead cannot have been missed, and counting it dilutes the rate
  // by an order of magnitude — October's CM intakes read 1.33% over the row and
  // 12.28% over the resolved ones. This is the same definition the daily ops report
  // already uses; the row-total version quietly contradicted it (Ohad, 2026-10-04).
  const noShowPct = (t: string) => {
    const done = matrix[t]?.done ?? 0;
    const miss = matrix[t]?.noshow ?? 0;
    const held = done + miss;
    return held > 0 && miss > 0 ? `${((miss / held) * 100).toFixed(2)}%` : '';
  };
  const noShowPctTotal = () => {
    const held = (statusCounts.done ?? 0) + (statusCounts.noshow ?? 0);
    const miss = statusCounts.noshow ?? 0;
    return held > 0 && miss > 0 ? `${((miss / held) * 100).toFixed(2)}%` : '';
  };

  // ── The same selection, laid out along the month axis ──
  // `month` is already on every granular row; the ordinary summary folds it away, so
  // comparing periods meant running the query twice and reading two tables (Ohad,
  // 2026-09-10 — "משווה ממש אחד ליד השני, לא כל טבלה בנפרד").
  const cmpMonths = useMemo(() => {
    const seen = new Set<string>();
    for (const r of granSel) seen.add(r.month);
    return Array.from(seen).sort();
  }, [granSel]);
  const monthLabel = (id: string) => MONTH_OPTS.find((o) => o.id === id)?.label ?? id;
  // type → month → status → n. Built from granSel, so every filter already applies.
  const monthMatrix = useMemo(() => {
    const m: Record<string, Record<string, Record<string, number>>> = {};
    for (const t of TYPE_ORDER) m[t] = {};
    for (const r of granSel) {
      const b = bucketOf(r.role, r.kind);
      if (!m[b]) continue;
      if (!m[b][r.month]) m[b][r.month] = {};
      m[b][r.month][r.status] = (m[b][r.month][r.status] ?? 0) + r.n;
    }
    return m;
  }, [granSel]);
  const cellTotal = (t: string, mo: string) =>
    visStatuses.reduce((a, st) => a + (monthMatrix[t]?.[mo]?.[st] ?? 0), 0);
  // Only rows that actually have meetings. Six kinds x two roles is twelve combinations,
  // and some cannot occur at all — a case manager never has a double follow-up — so an
  // always-empty row reads as a category that exists and happens to be zero this month.
  const visTypes = useMemo(
    () => selTypes.filter((t) => visStatuses.some((st) => (matrix[t]?.[st] ?? 0) > 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selTypes.join('|'), visStatuses.join('|'), matrix],
  );

  const colTotal = (mo: string) => visTypes.reduce((a, t) => a + cellTotal(t, mo), 0);

  // Drill from a summary number → narrow to that type (+status), switch to detailed, apply now.
  const drill = (type: string, status?: string) => {
    const roles = new Set([BUCKET_ROLE[type]]);
    const kinds = new Set([BUCKET_KIND[type]]);
    const statuses = status ? new Set([status]) : new Set(dStatuses);
    setDRoles(roles); setDKinds(kinds); if (status) setDStatuses(statuses); setDMode('detailed');
    // Marks carry through the drill: clicking a number in the summary narrows the type,
    // it does not silently widen the mark selection you already made.
    setApplied({ months: [...dMonths], roles, kinds, statuses, clinics: new Set(dClinics), therapists: new Set(dTherapists), mode: 'detailed', q: qDraft,
      dateFrom: dFrom, dateTo: dTo, pastOnly: dPastOnly, compare: [...dCompare] });
    setPage(1);
  };

  // Narrow to one month from the month table. It also clears the day range, because a
  // range OVERRIDES the months (see rangeWins) — leaving it set would make the click do
  // nothing and read as a broken button.
  const pickMonth = (id: string) => {
    setDMonths([id]); setDFrom(''); setDTo(''); setDCompare([]);
    setApplied({ months: [id], roles: new Set(dRoles), kinds: new Set(dKinds),
      statuses: new Set(dStatuses), clinics: new Set(dClinics), therapists: new Set(dTherapists),
      mode: 'summary', q: qDraft, dateFrom: '', dateTo: '', pastOnly: dPastOnly,
      compare: [] });
    setPage(1);
  };

  // ── Report CSV (materialise empty=all sets to full sets for the existing builder) ──
  const mat = (s: Set<string>, full: string[]) => (s.size ? s : new Set(full));
  const reportSets = {
    roles: mat(applied.roles, ROLES), kinds: mat(applied.kinds, KIND_ORDER),
    statuses: mat(applied.statuses, STATUS_ORDER), clinics: mat(applied.clinics, clinicOptions),
    therapists: mat(applied.therapists, therapistOptions),
  };

  // ── Detailed list ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: any[] = listData?.rows ?? [];
  const total: number = listData?.total ?? 0;
  const totalMinutes: number = listData?.total_minutes ?? 0;
  const totalHours = Math.round(totalMinutes / 60 * 100) / 100; // two decimals
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cancelSummary: any = listData?.cancel_summary ?? null;
  const kindCounts: Record<string, number> = listData?.kind_counts ?? {};

  // ── Column headers: filter the value columns, flip the direction of the ordered ones ──
  // The value columns reuse the SAME state the chips above use, so a filter set from the
  // header shows up as a chip and the two can never disagree. Two columns (נוער/מבוגר,
  // מי קבע) have no chip and keep their own state.
  const COLUMNS: { label: string; sort?: string; filter?: string }[] = [
    { label: 'תאריך', sort: 'date' },
    { label: 'שעה', sort: 'time' },
    { label: 'מטפל', filter: 'therapists' },
    { label: 'סוג', filter: 'kinds' },
    { label: 'נוער/מבוגר', filter: 'patientTypes' },
    { label: 'מטופל' },
    { label: 'קופה', filter: 'clinics' },
    { label: 'סטטוס', filter: 'statuses' },
    { label: 'סלוט', sort: 'slot' },
    { label: 'מי קבע', filter: 'whos' },
    { label: 'בוטל לפני', sort: 'lead' },
    { label: 'סיבה' },
  ];

  const colOptions = (col: string): { value: string; label: string }[] => {
    if (col === 'therapists') {
      const present = new Set(granExcept('therapists').map((r) => r.therapist));
      return therapistOptions.filter((t) => present.has(t)).map((t) => ({ value: t, label: t }));
    }
    if (col === 'kinds') {
      const present = new Set(granExcept('kinds').map((r) => r.kind));
      return KIND_OPTS.filter((o) => present.has(o.value));
    }
    if (col === 'patientTypes') return [{ value: 'Adult', label: 'מבוגר' }, { value: 'Youth', label: 'נוער' }];
    if (col === 'clinics') {
      const present = new Set(granExcept('clinics').map((r) => r.clinic));
      return clinicOptions.filter((c) => present.has(c)).map((c) => ({ value: c, label: c }));
    }
    if (col === 'statuses') {
      const present = new Set(granExcept('statuses').map((r) => r.status));
      return STATUS_OPTS.filter((o) => present.has(o.value));
    }
    return Object.entries(WHO_LABEL).map(([value, label]) => ({ value, label: value === 'unknown' ? 'לא ידוע' : label }));
  };

  const colFilterOf = (col: string): Set<string> => {
    if (col === 'patientTypes') return colPatientTypes;
    if (col === 'whos') return colWhos;
    return applied[col as 'therapists' | 'kinds' | 'clinics' | 'statuses'];
  };

  const setColFilter = (col: string, next: Set<string>) => {
    setPage(1);
    if (col === 'patientTypes') { setColPatientTypes(next); return; }
    if (col === 'whos') { setColWhos(next); return; }
    // Keep draft and applied in step, so the panel above shows what the table is doing
    // instead of lighting up "יש שינויים" for a change the user already sees applied.
    const setDraft = { therapists: setDTherapists, kinds: setDKinds, clinics: setDClinics, statuses: setDStatuses }[col];
    setDraft?.(next);
    setApplied((a) => ({ ...a, [col]: next }));
  };

  const toggleSort = (key: string) => {
    setPage(1);
    setSort((cur) => (cur === `${key}_desc` ? `${key}_asc` : `${key}_desc`));
  };

  const ColumnMenu = ({ column, onClose }: { column: string; onClose: () => void }) => {
    const sel = colFilterOf(column);
    const all = colOptions(column);
    // Search appears only when the list is long enough to need it — on four values it
    // would be one more thing to look at for nothing.
    const [find, setFind] = useState('');
    const opts = find ? all.filter((o) => o.label.toLowerCase().includes(find.toLowerCase())) : all;
    return (
      <>
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
        <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 41, minWidth: 170, maxHeight: 280,
          overflowY: 'auto', background: 'var(--ex-bg-card)', border: '1px solid var(--ex-border)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.35)', padding: 6, textAlign: 'right' }}>
          {all.length > 8 && (
            <input autoFocus value={find} onChange={(e) => setFind(e.target.value)} placeholder="חיפוש…"
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: 4, padding: '4px 8px', fontSize: 12.5,
                background: 'var(--ex-bg-soft)', color: 'var(--ex-text-primary)',
                border: '1px solid var(--ex-border)', borderRadius: 6, textAlign: 'right' }} />
          )}
          <button type="button" onClick={() => { setColFilter(column, new Set()); onClose(); }}
            style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%', padding: '5px 8px',
              fontSize: 12.5, fontWeight: sel.size === 0 ? 700 : 400,
              color: sel.size === 0 ? 'var(--ex-blue-text)' : 'var(--ex-text-muted)' }}>
            הכל
          </button>
          {opts.length === 0 && (
            <div style={{ padding: '6px 8px', fontSize: 12, color: 'var(--ex-text-muted)' }}>אין תוצאות</div>
          )}
          {opts.map((o) => (
            <button key={o.value} type="button"
              onClick={() => {
                const next = new Set(sel);
                if (next.has(o.value)) next.delete(o.value); else next.add(o.value);
                setColFilter(column, next);
              }}
              style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%', padding: '5px 8px',
                fontSize: 12.5, whiteSpace: 'nowrap',
                color: sel.has(o.value) ? 'var(--ex-blue-text)' : 'var(--ex-text-primary)',
                fontWeight: sel.has(o.value) ? 700 : 400 }}>
              {sel.has(o.value) ? '✓ ' : ''}{o.label}
            </button>
          ))}
        </div>
      </>
    );
  };

  const exportListCsv = async () => {
    const head = ['תאריך', 'שעה', 'מטפל', 'סוג', 'נוער/מבוגר', 'מטופל', 'קופה', 'סטטוס', 'סלוט (דק׳)', 'מי קבע', 'בוטל לפני (שעות)', 'סיבה'];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const all: any[] = [];
    for (let p = 1; p <= 60 && all.length < total; p++) {
      // eslint-disable-next-line no-await-in-loop
      const r = await execApi.getMeetingsExplorer({
        backMonths: applied.months, clinics: appliedClinics, typeIds: appliedTypeIds,
        statuses: appliedStatuses, therapists: appliedTherapists, kinds: appliedKinds,
        patientTypes: colPts, whos: colWhoList, sort,
        q: applied.q, page: p, pageSize: 200,
      });
      all.push(...(r.data?.rows ?? []));
      if (!(r.data?.rows ?? []).length) break;
    }
    const lines = all.map((r) => [
      fmtDate(r.date), r.time ?? '', r.therapist, rowKindLabel(r.type, r.kind),
      r.patient_type === 'Youth' ? 'נוער' : 'מבוגר', r.patient, r.clinic, STATUS_LABEL[r.status] ?? r.status,
      r.slot_minutes ?? '', WHO_LABEL[r.who] ?? r.who, r.lead_hours ?? '', (r.reason || '').replace(/\n/g, ' '),
    ]);
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = '﻿' + [head, ...lines].map((row) => row.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `meetings_${monthsKey || `${applied.dateFrom || 'start'}_${applied.dateTo || 'today'}`}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const td: React.CSSProperties = { padding: '6px 12px', whiteSpace: 'nowrap' };
  const summaryEmpty = visTypes.length === 0 || visStatuses.length === 0;

  return (
    <div dir="rtl" style={{ position: 'relative', color: 'var(--ex-text-primary)', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 700 }}>סייר הפגישות</h2>
        <p style={{ fontSize: 13, color: 'var(--ex-text-muted)' }}>בחר את הסינון ולחץ <b>הצג נתונים</b>. בברירת מחדל מוצג החודש הנוכחי, כל הפגישות, בתצוגת סיכום.</p>
      </div>

      {/* ── The one unified filter panel ── */}
      <div style={{ ...CARD, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 9 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ex-text-secondary)' }}>סינון</span>
          <button type="button" onClick={() => buildReportCsv(gran, applied.months, applied.mode, reportSets)}
            style={{ background: 'transparent', color: 'var(--ex-text-muted)', border: '1px solid var(--ex-border)', borderRadius: 8, padding: '6px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>יצירת דוחות ↓ CSV</button>
        </div>

        <PanelField label="תקופה">
         <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button type="button" onClick={() => setMonthsOpen((o) => !o)}
              title={rangeWins ? 'טווח התאריכים גובר — נקה אותו כדי לסנן לפי חודשים' : undefined}
              style={{ background: 'var(--ex-bg-soft)', color: rangeWins ? 'var(--ex-text-muted)' : 'var(--ex-text-primary)', border: '1px solid var(--ex-border)', borderRadius: 7, padding: '5px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 150, textAlign: 'right', opacity: rangeWins ? 0.5 : 1, textDecoration: rangeWins ? 'line-through' : 'none' }}>
              {monthsLabel} ▾
            </button>
            {monthsOpen && (
              <>
                <div onClick={() => setMonthsOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
                <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 21, marginTop: 4, background: 'var(--ex-bg-card)', border: '1px solid var(--ex-border)', borderRadius: 8, padding: 6, maxHeight: 300, overflowY: 'auto', minWidth: 190, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                  <div style={{ display: 'flex', gap: 6, padding: '2px 4px 6px', borderBottom: '1px solid var(--ex-border)', marginBottom: 4 }}>
                    <button type="button" onClick={() => setDMonths(Array.from({ length: 12 }, (_, i) => `2026-${String(i + 1).padStart(2, '0')}`))}
                      style={{ flex: 1, fontSize: 12, background: 'var(--ex-blue-bar)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 600 }}>כל 2026</button>
                    <button type="button" onClick={() => setDMonths(Array.from({ length: 12 }, (_, i) => `2026-${String(i + 1).padStart(2, '0')}`).filter((m) => m >= curMonthId))}
                      style={{ flex: 1, fontSize: 12, background: 'var(--ex-blue-bar)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 600 }}>קדימה →</button>
                    <button type="button" onClick={() => setDMonths([curMonthId])}
                      style={{ flex: 1, fontSize: 12, background: 'none', color: 'var(--ex-text-muted)', border: '1px solid var(--ex-border)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>חודש נוכחי</button>
                  </div>
                  {MONTH_OPTS.map((m) => (
                    <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer', fontSize: 13, color: 'var(--ex-text-secondary)' }}>
                      <input type="checkbox" checked={dMonths.includes(m.id)} onChange={() => toggleMonth(m.id)} />
                      {m.label}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* The range sits HERE, beside the months, because the two answer the same
              question and were read as independent when they were rows apart: picking
              August and then a range back to last September returned August alone.
              It now WINS over the months (see _window_sql), and the months grey out to
              say so before the query runs rather than after. */}
          <span style={{ width: 1, height: 20, background: 'var(--ex-border)', margin: '0 4px' }} />
          <span style={{ fontSize: 12, color: 'var(--ex-text-muted)' }}>או טווח תאריכים</span>
          <input type="date" value={dFrom} max={dTo || undefined} onChange={(e) => setDFrom(e.target.value)}
            title="מתאריך"
            style={{ background: 'var(--ex-bg-soft)', color: 'var(--ex-text)', border: `1px solid ${rangeWins ? 'var(--ex-blue-border)' : 'var(--ex-border)'}`, borderRadius: 8, padding: '4px 10px', fontSize: 12.5 }} />
          <span style={{ fontSize: 12, color: 'var(--ex-text-muted)' }}>עד</span>
          <input type="date" value={dTo} min={dFrom || undefined} onChange={(e) => setDTo(e.target.value)}
            title="עד תאריך"
            style={{ background: 'var(--ex-bg-soft)', color: 'var(--ex-text)', border: `1px solid ${rangeWins ? 'var(--ex-blue-border)' : 'var(--ex-border)'}`, borderRadius: 8, padding: '4px 10px', fontSize: 12.5 }} />
          {/* Comparison sits beside the period because it is the same question asked
              twice. Picking months here IS the switch — there is no separate toggle to
              leave on by accident (Ohad, 2026-09-10). */}
          <span style={{ width: 1, height: 20, background: 'var(--ex-border)', margin: '0 4px' }} />
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button type="button" onClick={() => setCmpOpen((o) => !o)}
              title={rangeWins ? 'טווח התאריכים גובר — נקה אותו כדי להשוות חודשים' : 'בחר תקופה להשוואה'}
              style={{ background: dCompare.length ? 'var(--ex-blue)' : 'var(--ex-bg-soft)', color: dCompare.length ? 'var(--ex-blue-text)' : 'var(--ex-text-primary)', border: `1px solid ${dCompare.length ? 'var(--ex-blue-border)' : 'var(--ex-border)'}`, borderRadius: 7, padding: '5px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 150, textAlign: 'right', opacity: rangeWins ? 0.5 : 1, textDecoration: rangeWins ? 'line-through' : 'none' }}>
              {dCompare.length === 0 ? 'השוואה' : dCompare.length === 1 ? `השוואה · ${MONTH_OPTS.find((m) => m.id === dCompare[0])?.label}` : `השוואה · ${dCompare.length} חודשים`} ▾
            </button>
            {cmpOpen && (
              <>
                <div onClick={() => setCmpOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
                <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 21, marginTop: 4, background: 'var(--ex-bg-card)', border: '1px solid var(--ex-border)', borderRadius: 8, padding: 6, maxHeight: 300, overflowY: 'auto', minWidth: 210, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                  <div style={{ padding: '2px 6px 6px', borderBottom: '1px solid var(--ex-border)', marginBottom: 4, fontSize: 12, color: 'var(--ex-text-muted)' }}>
                    בחר תקופה להשוואה — הסיכום יוצג חודש מול חודש
                  </div>
                  {/* The month already chosen as the period is not offered again: it is
                      always a column, and ticking it here would read as a second copy. */}
                  {MONTH_OPTS.filter((m) => !dMonths.includes(m.id)).map((m) => (
                    <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer', fontSize: 13, color: 'var(--ex-text-secondary)' }}>
                      <input type="checkbox" checked={dCompare.includes(m.id)}
                        onChange={() => setDCompare((prev) => (prev.includes(m.id) ? prev.filter((x) => x !== m.id) : [...prev, m.id]))} />
                      {m.label}
                    </label>
                  ))}
                  {dCompare.length > 0 && (
                    <button type="button" onClick={() => setDCompare([])}
                      style={{ width: '100%', marginTop: 4, fontSize: 12, background: 'none', color: 'var(--ex-text-muted)', border: '1px solid var(--ex-border)', borderRadius: 6, padding: '5px 8px', cursor: 'pointer' }}>בטל השוואה</button>
                  )}
                </div>
              </>
            )}
          </div>

          {rangeWins && (
            <>
              {chipBtn('נקה טווח', false, () => { setDFrom(''); setDTo(''); }, 'clr')}
              <span style={{ fontSize: 11.5, color: 'var(--ex-blue-text)' }}>
                הטווח קובע — בחירת החודשים מבוטלת
              </span>
            </>
          )}
         </div>
        </PanelField>

        <PanelField label="תפקיד">{groupChips(ROLE_OPTS, dRoles, setDRoles)}</PanelField>
        <PanelField label="סוג">{groupChips(KIND_OPTS, dKinds, setDKinds)}</PanelField>
        <PanelField label="סטטוס">{groupChips(STATUS_OPTS, dStatuses, setDStatuses)}</PanelField>
        <PanelField label="קופה">{groupChips(clinicOptions.map((c) => ({ value: c, label: c })), dClinics, setDClinics)}</PanelField>

        <PanelField label="מטפל">
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button type="button" onClick={() => setThOpen((o) => !o)}
              style={{ background: dTherapists.size ? 'var(--ex-blue)' : 'var(--ex-bg-soft)', color: dTherapists.size ? 'var(--ex-blue-text)' : 'var(--ex-text-primary)', border: `1px solid ${dTherapists.size ? 'var(--ex-blue-border)' : 'var(--ex-border)'}`, borderRadius: 7, padding: '5px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', minWidth: 150, textAlign: 'right' }}>
              {thLabel} ▾
            </button>
            {thOpen && (
              <>
                <div onClick={() => setThOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
                <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 31, marginTop: 4, width: 280, background: 'var(--ex-bg-card)', border: '1px solid var(--ex-border)', borderRadius: 10, padding: 8, boxShadow: '0 12px 32px rgba(0,0,0,0.6)' }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <input value={thQuery} onChange={(e) => setThQuery(e.target.value)} placeholder="חיפוש שם…"
                      style={{ flex: 1, background: 'var(--ex-bg-soft)', color: 'var(--ex-text-primary)', border: '1px solid var(--ex-border)', borderRadius: 7, padding: '5px 9px', fontSize: 12.5 }} />
                    <button type="button" onClick={() => setDTherapists(new Set())} style={{ fontSize: 11.5, background: 'none', border: '1px solid var(--ex-border)', borderRadius: 6, color: 'var(--ex-text-muted)', padding: '4px 8px', cursor: 'pointer' }}>הכל</button>
                  </div>
                  <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {thShown.length === 0 && <span style={{ fontSize: 12, color: 'var(--ex-text-muted)', padding: 4 }}>—</span>}
                    {thShown.map((t) => (
                      <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '4px 5px', cursor: 'pointer', color: 'var(--ex-text-secondary)' }}>
                        <input type="checkbox" checked={dTherapists.size === 0 || dTherapists.has(t)} onChange={() => clickTherapist(t)} />
                        {t}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </PanelField>

        <PanelField label="מסננים">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {chipBtn('עבר זמנן', dPastOnly, () => setDPastOnly(!dPastOnly), 'past')}
            <span style={{ fontSize: 11.5, color: 'var(--ex-text-muted)' }}>
              (עם סטטוס "פעילה" — פגישות שלא נסגרו)
            </span>
          </div>
        </PanelField>

        <PanelField label="פירוט">
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {([['summary', 'סיכום'], ['detailed', 'מפורט']] as const).map(([v, l]) => chipBtn(l, dMode === v, () => setDMode(v), v))}
          </div>
        </PanelField>

        {/* Apply — nothing fetches until this is pressed. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
          <button type="button" onClick={applyNow}
            style={{ background: dirty ? 'var(--ex-blue-bar)' : 'var(--ex-bg-soft)', color: dirty ? '#fff' : 'var(--ex-text-muted)', border: `1px solid ${dirty ? 'var(--ex-blue-border)' : 'var(--ex-border)'}`, borderRadius: 8, padding: '8px 22px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
            הצג נתונים
          </button>
          {dirty && <span style={{ fontSize: 12, color: 'var(--ex-amber, #92400E)' }}>יש שינויים בסינון — לחץ "הצג נתונים"</span>}
        </div>
      </div>

      {/* ── SUMMARY ── */}
      {applied.mode === 'summary' && (
        <div style={{ ...CARD, padding: 12, overflowX: 'auto' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ex-text-secondary)', marginBottom: 8 }}>
            {comparing
              ? 'השוואה בין תקופות · לחץ על שורה כדי לפתוח את הפירוט לפי סטטוס'
              : 'סיכום פגישות · לחץ על כל מספר כדי לפתוח את הרשימה המפורטת המסוננת'}
          </div>
          {granLoading ? (
            <div style={{ padding: '22px 8px', fontSize: 13.5, color: 'var(--ex-text-muted)', textAlign: 'center' }}>טוען…</div>
          ) : summaryEmpty ? (
            <div style={{ padding: '22px 8px', fontSize: 13.5, color: 'var(--ex-text-muted)', textAlign: 'center' }}>אין נתונים לסינון הנוכחי.</div>
          ) : comparing ? (
            /* Months as COLUMNS, one row per type: what moved is read across a single
               line instead of between two tables. The per-status split is the second
               question and opens only on click. */
            <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: 'var(--ex-text-muted)', fontSize: 12 }}>
                  <th style={{ textAlign: 'right', fontWeight: 600, padding: '4px 10px' }}>סוג</th>
                  {cmpMonths.map((mo) => (
                    <th key={mo} style={{ fontWeight: 700, padding: '4px 10px', textAlign: 'center' }}>
                      {/* Clicking a column zooms into that month alone — the way out of a
                          comparison is the same gesture as every other narrowing here. */}
                      <button type="button" onClick={() => pickMonth(mo)} title={`רק ${monthLabel(mo)}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit',
                                 color: applied.months.includes(mo) ? 'var(--ex-blue-text)' : 'var(--ex-text-secondary)' }}>
                        {monthLabel(mo)}
                      </button>
                    </th>
                  ))}
                  <th style={{ fontWeight: 700, padding: '4px 10px', textAlign: 'center', color: 'var(--ex-text-muted)' }}>שינוי</th>
                </tr>
              </thead>
              <tbody>
                {visTypes.map((t) => {
                  const first = cellTotal(t, cmpMonths[0]);
                  const last = cellTotal(t, cmpMonths[cmpMonths.length - 1]);
                  const delta = first ? ((last - first) / first) * 100 : null;
                  const open = openType === t;
                  return (
                    <Fragment key={t}>
                      <tr style={{ borderTop: '1px solid var(--ex-border)', cursor: 'pointer' }}
                          onClick={() => setOpenType(open ? null : t)}>
                        <td style={{ textAlign: 'right', padding: '6px 10px' }}>
                          <span style={{ color: 'var(--ex-text-muted)', marginLeft: 6 }}>{open ? '▾' : '▸'}</span>
                          {TYPE_LABEL[t]}
                        </td>
                        {cmpMonths.map((mo) => (
                          <td key={mo} style={{ textAlign: 'center', padding: '6px 10px', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--ex-text-primary)' }}>
                            {cellTotal(t, mo).toLocaleString()}
                          </td>
                        ))}
                        <td style={{ textAlign: 'center', padding: '6px 10px', fontSize: 13, fontVariantNumeric: 'tabular-nums',
                                     color: delta == null ? 'var(--ex-text-muted)' : delta >= 0 ? '#047857' : '#EF4444' }}>
                          {delta == null ? '—' : `${delta >= 0 ? '+' : ''}${delta.toFixed(2)}%`}
                        </td>
                      </tr>
                      {open && visStatuses.map((st) => {
                        // Each status carries its own trend. Without it the breakdown said
                        // WHAT each month held but not which part of it moved — and no-show
                        // moving is the whole reason to open the row (Ohad, 2026-09-10).
                        const sFirst = monthMatrix[t]?.[cmpMonths[0]]?.[st] ?? 0;
                        const sLast = monthMatrix[t]?.[cmpMonths[cmpMonths.length - 1]]?.[st] ?? 0;
                        const sDelta = sFirst ? ((sLast - sFirst) / sFirst) * 100 : null;
                        return (
                          <tr key={`${t}-${st}`} style={{ background: 'var(--ex-bg-soft)' }}>
                            <td style={{ textAlign: 'right', padding: '4px 10px 4px 26px', fontSize: 12.5, color: STATUS_COLOR[st] }}>
                              {STATUS_LABEL[st]}
                            </td>
                            {cmpMonths.map((mo) => {
                              // The count AND its share of that month. A no-show count on
                              // its own cannot be compared across months of different size:
                              // 5 of 171 and 2 of 57 are the same month, and only the share
                              // says so (Ohad, 2026-09-10).
                              const n = monthMatrix[t]?.[mo]?.[st] ?? 0;
                              const of = cellTotal(t, mo);
                              return (
                                <td key={mo} style={{ textAlign: 'center', padding: '4px 10px', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: 'var(--ex-text-secondary)' }}>
                                  {n.toLocaleString()}
                                  {of > 0 && (
                                    <span style={{ color: 'var(--ex-text-muted)', fontSize: 11.5, marginRight: 5 }}>
                                      {((n / of) * 100).toFixed(2)}%
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                            <td style={{ textAlign: 'center', padding: '4px 10px', fontSize: 12.5, fontVariantNumeric: 'tabular-nums',
                                         color: sDelta == null ? 'var(--ex-text-muted)' : sDelta >= 0 ? '#047857' : '#EF4444' }}>
                              {sDelta == null ? '—' : `${sDelta >= 0 ? '+' : ''}${sDelta.toFixed(2)}%`}
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
                <tr style={{ borderTop: '2px solid var(--ex-border)' }}>
                  <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, color: 'var(--ex-text-secondary)' }}>סה״כ</td>
                  {cmpMonths.map((mo) => (
                    <td key={mo} style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 800, fontSize: 18, fontVariantNumeric: 'tabular-nums', color: 'var(--ex-text-primary)' }}>
                      {colTotal(mo).toLocaleString()}
                    </td>
                  ))}
                  <td style={{ textAlign: 'center', padding: '8px 10px', fontSize: 13, fontVariantNumeric: 'tabular-nums',
                               color: colTotal(cmpMonths[0]) === 0 ? 'var(--ex-text-muted)'
                                 : colTotal(cmpMonths[cmpMonths.length - 1]) >= colTotal(cmpMonths[0]) ? '#10B981' : '#EF4444' }}>
                    {colTotal(cmpMonths[0]) === 0 ? '—'
                      : `${colTotal(cmpMonths[cmpMonths.length - 1]) >= colTotal(cmpMonths[0]) ? '+' : ''}${(((colTotal(cmpMonths[cmpMonths.length - 1]) - colTotal(cmpMonths[0])) / colTotal(cmpMonths[0])) * 100).toFixed(2)}%`}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: 'var(--ex-text-muted)', fontSize: 12 }}>
                  <th style={{ textAlign: 'right', fontWeight: 600, padding: '4px 10px' }}>סוג</th>
                  {visStatuses.map((s) => (
                    <th key={s} style={{ fontWeight: 700, padding: '4px 10px', color: STATUS_COLOR[s], textAlign: 'center' }}>{STATUS_LABEL[s]}</th>
                  ))}
                  <th style={{ fontWeight: 700, padding: '4px 10px', textAlign: 'center', color: 'var(--ex-text-secondary)' }}>סה״כ</th>
                  <th style={{ fontWeight: 700, padding: '4px 10px', textAlign: 'center', color: 'var(--ex-text-secondary)' }}
                    title="שעות עבודה בפועל — סכום אורכי הפגישות שלא בוטלו">שעות</th>
                </tr>
              </thead>
              <tbody>
                {visTypes.map((t) => {
                  const rowTotal = visStatuses.reduce((a, s) => a + (matrix[t]?.[s] ?? 0), 0);
                  return (
                    <tr key={t} style={{ borderTop: '1px solid var(--ex-border)' }}>
                      <td style={{ textAlign: 'right', padding: '6px 10px' }}>
                        <button onClick={() => drill(t)} title="כל הפגישות מסוג זה"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>{TYPE_LABEL[t]}</button>
                      </td>
                      {visStatuses.map((s) => (
                        <td key={s} style={{ textAlign: 'center', padding: '6px 10px' }}>
                          <button onClick={() => drill(t, s)} title={s === 'noshow'
                              ? `${TYPE_LABEL[t]} · אי-הגעה · ${noShowPct(t)} מתוך הפגישות שנסגרו (בוצעו + אי-הגעה). פגישה עתידית אינה נספרת.`
                              : `${TYPE_LABEL[t]} · ${STATUS_LABEL[s]}`}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontVariantNumeric: 'tabular-nums', color: 'var(--ex-text-primary)', fontWeight: 600 }}>
                            {/* Out of the ROW, never the column: "how much of psychiatrist
                                intake was a no-show" is the question this table gets asked,
                                and a share of the column would answer a different one.
                                Rendered BEFORE the count so RTL puts it on the number's
                                left, in the no-show red (Ohad, 2026-10-04). */}
                            {s === 'noshow' && (
                              <span style={{ color: STATUS_COLOR.noshow, fontWeight: 400, fontSize: 12, marginLeft: 5 }}>
                                {noShowPct(t)}{'\u00A0'}
                              </span>
                            )}
                            {(matrix[t]?.[s] ?? 0).toLocaleString()}
                          </button>
                        </td>
                      ))}
                      <td style={{ textAlign: 'center', padding: '6px 10px' }}>
                        <button onClick={() => drill(t)} title="כל הפגישות מסוג זה"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, color: 'inherit' }}>{rowTotal.toLocaleString()}</button>
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 10px', fontVariantNumeric: 'tabular-nums', color: 'var(--ex-text-secondary)' }}>
                        {fmtHours(hoursByType[t] ?? 0)}
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop: '2px solid var(--ex-border)' }}>
                  <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, color: 'var(--ex-text-secondary)' }}>סה״כ</td>
                  {visStatuses.map((s) => (
                    <td key={s} style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 800, fontSize: 18, color: STATUS_COLOR[s], fontVariantNumeric: 'tabular-nums' }}>
                      {s === 'noshow' && (
                        <span style={{ color: STATUS_COLOR.noshow, fontWeight: 400, fontSize: 13, marginLeft: 6 }}>
                          {noShowPctTotal()}{'\u00A0'}
                        </span>
                      )}
                      {(statusCounts[s] ?? 0).toLocaleString()}
                    </td>
                  ))}
                  <td style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 800, fontSize: 18, color: 'var(--ex-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                    {visStatuses.reduce((a, s) => a + (statusCounts[s] ?? 0), 0).toLocaleString()}
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 800, fontSize: 18, color: 'var(--ex-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtHours(visTypes.reduce((a, t) => a + (hoursByType[t] ?? 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Per-therapist, under the type matrix and driven by the same filters. Pick
          "מעקב אד-הוק" above and this becomes the ad-hoc-per-case-manager table Shira
          asked for; leave the filters wide and it is simply who did the work.
          Cancellations are out, for the same reason they are out of the hours column. */}
      {applied.mode === 'summary' && byTherapist.length > 0 && (
        <div style={{ ...CARD, padding: '12px 16px' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            לפי מטפל
            <span style={{ fontWeight: 400, color: 'var(--ex-text-muted)' }}>
              {' '}· {byTherapist.length} מטפלים · לפי הסינון שלמעלה · בלי ביטולים
            </span>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: 'var(--ex-text-muted)', fontSize: 12 }}>
                  <th style={{ textAlign: 'right', fontWeight: 600, padding: '4px 10px' }}>מטפל</th>
                  <th style={{ textAlign: 'center', fontWeight: 700, padding: '4px 10px' }}>פגישות</th>
                  <th style={{ textAlign: 'center', fontWeight: 700, padding: '4px 10px' }}>שעות</th>
                </tr>
              </thead>
              <tbody>
                {byTherapist.map((t) => (
                  <tr key={t.therapist} style={{ borderTop: '1px solid var(--ex-border)' }}>
                    <td style={{ padding: '6px 10px' }}>{t.therapist}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                      {t.n.toLocaleString()}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'center', fontVariantNumeric: 'tabular-nums',
                                 color: 'var(--ex-text-secondary)' }}>
                      {fmtHours(t.minutes)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--ex-border)', fontWeight: 700 }}>
                  <td style={{ padding: '6px 10px' }}>סה״כ</td>
                  <td style={{ padding: '6px 10px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                    {byTherapist.reduce((a, t) => a + t.n, 0).toLocaleString()}
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtHours(byTherapist.reduce((a, t) => a + t.minutes, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── DETAILED LIST ── */}
      {applied.mode === 'detailed' && (<>
        {cancelSummary && (
          <div style={{ ...CARD, padding: '12px 16px' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ex-text-secondary)', marginBottom: 8 }}>ניתוח ביטולים</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
              <div><div style={{ fontSize: 22, fontWeight: 800 }}>{cancelSummary.canceled.toLocaleString()}</div><div style={{ fontSize: 12, color: 'var(--ex-text-muted)' }}>בוטלו</div></div>
              <div><div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ex-green)' }}>{cancelSummary.refilled.toLocaleString()} · {cancelSummary.refill_pct}%</div><div style={{ fontSize: 12, color: 'var(--ex-text-muted)' }}>אוישו מחדש (מטופל אחר תפס את הסלוט)</div></div>
              <div><div style={{ fontSize: 22, fontWeight: 800 }}>{cancelSummary.by_clinic.toLocaleString()}</div><div style={{ fontSize: 12, color: 'var(--ex-text-muted)' }}>בוטל ע״י קליניקה</div></div>
              <div><div style={{ fontSize: 22, fontWeight: 800 }}>{cancelSummary.by_client.toLocaleString()}</div><div style={{ fontSize: 12, color: 'var(--ex-text-muted)' }}>בוטל ע״י לקוח</div></div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 14 }}>
          <input placeholder="חיפוש מטפל/מטופל (Enter)" defaultValue={applied.q}
            onKeyDown={(e) => { if (e.key === 'Enter') { setQDraft((e.target as HTMLInputElement).value); setApplied((a) => ({ ...a, q: (e.target as HTMLInputElement).value })); setPage(1); } }}
            style={{ background: 'var(--ex-bg-soft)', color: 'var(--ex-text-primary)', border: '1px solid var(--ex-border)', borderRadius: 7, padding: '6px 10px', flex: 1, minWidth: 180 }} />
          <button onClick={exportListCsv} style={{ background: 'var(--ex-green-bar)', color: '#101D35', border: 'none', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>יצא CSV</button>
          <span style={{ color: 'var(--ex-text-muted)' }}>{listLoading ? 'טוען…' : `${total.toLocaleString()} פגישות`}</span>
          {!listLoading && total > 0 && (
            <span style={{ color: 'var(--ex-text-secondary)', fontWeight: 600 }}>· סה״כ {totalHours.toLocaleString()} שעות</span>
          )}
          {/* Counted over the whole filtered set, not this page — this is the answer to
              "how many double follow-ups were there in the last two months". */}
          {!listLoading && (kindCounts.double > 0 || kindCounts.reintake > 0) && (
            <span style={{ color: 'var(--ex-text-muted)' }}>
              {kindCounts.double > 0 && <>· <b style={{ color: KIND_COLOR.double }}>{kindCounts.double.toLocaleString()}</b> מעקבים כפולים </>}
              {kindCounts.reintake > 0 && <>· <b style={{ color: KIND_COLOR.reintake }}>{kindCounts.reintake.toLocaleString()}</b> ריאינטייקים</>}
            </span>
          )}
        </div>

        <div style={{ ...CARD, overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 14 }}>
            <thead style={{ background: 'var(--ex-bg-soft)', color: 'var(--ex-text-muted)', fontSize: 12 }}>
              <tr>
                {COLUMNS.map((c) => {
                  const sorted = c.sort && sort.startsWith(`${c.sort}_`);
                  const active = c.filter ? colFilterOf(c.filter).size > 0 : false;
                  const plain = !c.sort && !c.filter;
                  return (
                    <th key={c.label} style={{ textAlign: 'right', padding: 0, fontWeight: 400, whiteSpace: 'nowrap', position: 'relative' }}>
                      <button type="button" disabled={plain}
                        onClick={() => (c.sort ? toggleSort(c.sort) : setOpenCol(openCol === c.label ? null : c.label))}
                        style={{ all: 'unset', cursor: plain ? 'default' : 'pointer', padding: '8px 12px', display: 'block', width: '100%',
                          color: sorted || active ? 'var(--ex-blue-text)' : 'inherit', fontWeight: sorted || active ? 700 : 400 }}>
                        {c.label}
                        {sorted && <span style={{ marginRight: 4 }}>{sort.endsWith('_asc') ? '▲' : '▼'}</span>}
                        {active && <span style={{ marginRight: 4 }}>●</span>}
                        {c.filter && !active && <span style={{ marginRight: 4, opacity: 0.45 }}>▾</span>}
                      </button>
                      {openCol === c.label && c.filter && (
                        <ColumnMenu column={c.filter} onClose={() => setOpenCol(null)} />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--ex-border)' }}>
                  <td style={td}>{fmtDate(r.date)}</td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.time || '—'}</td>
                  <td style={td}>{r.therapist}</td>
                  <td style={{ padding: '6px 12px', whiteSpace: 'nowrap',
                    color: KIND_COLOR[r.kind] ?? 'inherit', fontWeight: KIND_COLOR[r.kind] ? 700 : 400 }}>
                    {rowKindLabel(r.type, r.kind)}
                  </td>
                  <td style={{ padding: '6px 12px', color: r.patient_type === 'Youth' ? 'var(--ex-blue-text)' : 'var(--ex-text-secondary)' }}>{r.patient_type === 'Youth' ? 'נוער' : 'מבוגר'}</td>
                  <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                    <PatientCardLink linked={canOpenPatient} href={`${PATIENT_URL}?id=${r.internal_id}`}
                      style={{ color: canOpenPatient ? 'var(--ex-blue-text)' : 'var(--ex-text-secondary)' }}
                      title="פתח כרטיס מטופל">{r.patient}</PatientCardLink>
                  </td>
                  <td style={td}>{r.clinic}</td>
                  <td style={{ padding: '6px 12px' }}><span style={{ color: STATUS_COLOR[r.status], fontWeight: 600 }}>{STATUS_LABEL[r.status] ?? r.status}</span></td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.slot_minutes ? `${r.slot_minutes} דק׳` : '—'}</td>
                  <td style={{ padding: '6px 12px' }}>{WHO_LABEL[r.who] ?? r.who}</td>
                  <td style={td}>{r.status === 'canceled' ? fmtLead(r.lead_hours) : '—'}</td>
                  <td style={{ padding: '6px 12px', color: 'var(--ex-text-secondary)', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.reason}>{r.reason || '—'}</td>
                </tr>
              ))}
              {rows.length === 0 && !listLoading && (
                <tr><td colSpan={12} style={{ textAlign: 'center', color: 'var(--ex-text-muted)', padding: 24 }}>אין פגישות לסינון הנוכחי</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* The row RANGE, not just the page number. Shira reported paging "does not
            move" (2026-10-05): the pages do change, but 50 rows of the same month with
            the same therapists read as the same screen, and nothing on it said which
            rows these were. A range cannot be mistaken for standing still. */}
        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, fontSize: 14 }}>
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ background: 'var(--ex-bg-soft)', color: 'var(--ex-text-primary)', border: '1px solid var(--ex-border)', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', opacity: page <= 1 ? 0.4 : 1 }}>→ הקודם</button>
            <span style={{ color: 'var(--ex-text-secondary)' }}>
              מציג {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} מתוך {total}
              <span style={{ color: 'var(--ex-text-muted)' }}> · עמוד {page} מתוך {totalPages}</span>
            </span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ background: 'var(--ex-bg-soft)', color: 'var(--ex-text-primary)', border: '1px solid var(--ex-border)', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', opacity: page >= totalPages ? 0.4 : 1 }}>הבא ←</button>
          </div>
        )}
      </>)}
    </div>
  );
}
